// @ts-strict-ignore
import moment from 'moment';
import shortid from 'shortid';
import {
  ADDITIVE_PARENT_MAPPINGS,
  BRANCH_INFORMATION_INITIAL,
  CYBER_FRAUDS_INITIAL,
  ENTITY_ADDRESS_INITIAL,
  FINANCIAL_TINS_INITIAL,
  FRAUD_ID_SECTION_MAP,
  IP_ADDRESSES_INITIAL,
  NESTED_ADDITIVE_PARENT_MAPPINGS,
  PHONE_NUMBERS_INITIAL,
  QUESTION_24_CHECKBOX_INITIAL,
  QUESTION_56_CHECKBOX_INITIAL,
  QUESTION_53_CHECKBOX_INITIAL,
  QUESTION_54_CHECKBOX_INITIAL,
  QUESTION_68_CHECKBOX_INITIAL,
  QUESTION_80_CHECKBOX_INITIAL,
  RELATIONSHIP_TINS_INITIAL,
  SAR_ENTITY_IDS_INITIAL,
  INDEX_REFERENCE,
  PAGE_INDEX,
  PARENT_INDEX,
} from '../constants/form.constant';
import {
  initialFraudTypes,
  initialMetadataShape,
  initialSarEntity,
} from './state';
import { formatCurrencyIntl } from '~/helpers/i18n/';
import type { SarMetadatum } from '../types';
import type { UserContextValue } from '~/dashboard/context';

export const getValueFromPath = ({
  instanceOfCurrentPage,
  lookupObject,
  parentSectionIndex,
  path,
  sectionIndex,
}: {
  instanceOfCurrentPage?: number;
  lookupObject: object;
  parentSectionIndex?: number;
  path: Array<string>;
  sectionIndex?: number;
}) => {
  // lookup the next key in the path
  // if we find the INDEX_REFERENCE keyword, we'll use the provided sectionIndex
  // if we find the PATH_INDEX keyword, we'll use the provided instanceOfCurrentPage
  // if we find the PARENT_INDEX keyword, we'll use the provided parentSectionIndex
  let key = path[0];
  if (key === INDEX_REFERENCE) {
    key = `${sectionIndex}`;
  } else if (key === PAGE_INDEX) {
    key = `${instanceOfCurrentPage}`;
  } else if (key === PARENT_INDEX) {
    key = `${parentSectionIndex}`;
  }
  const value = lookupObject[key];
  // if this isn't the last key
  if (path[1] && value) {
    // recurse with the nested object and the rest of the path
    return getValueFromPath({
      instanceOfCurrentPage,
      lookupObject: value,
      parentSectionIndex,
      path: path.slice(1),
      sectionIndex,
    });
  }
  // if we reach the end of the path, return the found value
  return value;
};

// formatters:
export const nullIfEmptyString = string => (
  (typeof string === 'string' && string === '')
    ? null
    : string
);

export const trimInnerValues = input => {
  if (Array.isArray(input)) return input.map(trimInnerValues);
  if (!!(input) && typeof input === 'object') return Object.fromEntries(Object.entries(input).map(([k, v]) => [k, trimInnerValues(v)]));
  if (typeof input === 'string') return nullIfEmptyString(input.trim());
  return input;
};

export const nullOrFormatToCurrency = amount => ((amount || amount === 0)
  ? formatCurrencyIntl(amount, { alphabeticCurrencyCode: 'USD' }).formattedNumber
  : null);

export const nullOrFormatToInteger = amount => ((typeof amount === 'string')
  ? Math.trunc(Number(amount.replace(/[$,]/g, '')) * 100)
  : null);

export const utcDateToUsFormat = date => (
  moment.utc(date).isValid()
    ? moment.utc(date).format('MM/DD/YYYY')
    : (date || '')
);

export function validISOStringOrNull(date: string, format?: string): string | null {
  return moment.utc(date, format).isValid() ? moment.utc(date, format).toISOString() : null;
}

// form helpers:

const emptyStringIfNotString = string => (typeof string !== 'string' ? '' : string);

const prefillArrayIfEmpty = (valueArray, initialValue) => (
  !valueArray || valueArray.length === 0
    ? [initialValue]
    : valueArray
);

export const transformCheckboxObject = checkboxes => (
  // return an array of key strings where the value is true (box is checked)
  Object.keys(checkboxes).filter(key => checkboxes[key])
);

export const restoreDeletedPagesForAPI = (report, metadata, deletedPages) => {
  // add deleted report data and metadata pages back to their regular report data and metadata arrays
  Object.entries(deletedPages.report).forEach(([page, deletedPagesArray]) => {
    report[page] = report[page].concat(deletedPagesArray);
  });
  Object.entries(deletedPages.metadata).forEach(([page, deletedMetadataArray]) => {
    metadata[page] = (
      metadata[page] as Array<{ [prop: string]: Array<SarMetadatum> }>
    ).concat(deletedMetadataArray as Array<{ [prop: string]: Array<SarMetadatum> }>);
  });
};

const preProcessArrayValue = array => (
  array.map(value => {
    if (Array.isArray(value)) {
      return preProcessArrayValue(value);
    }
    if (!!value && typeof value === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return preProcessReport(value);
    }
    return nullIfEmptyString(value);
  })
);

const preProcessReport = report => (
  Object.fromEntries(Object.entries(report).map(
    ([key, value]) => {
      if (Array.isArray(value)) {
        const arrayValue = preProcessArrayValue(value);
        return [key, arrayValue];
      }
      if (!!value && typeof value === 'object') {
        return [key, preProcessReport(value)];
      }
      const otherValue = nullIfEmptyString(value);
      return [key, otherValue];
    },
  ))
);

// object mimics shape of state report, where only the values that need transformation exist
// map through keys
const transformReportRecursive = (report, map, entireReport) => {
  const transformed = Object.entries(map).reduce((acc, [key, value]) => {
    if (typeof value === 'function' && report[key] !== undefined) {
      acc[key] = value(report[key], entireReport);
    } else if (typeof value === 'object' && report[key] !== undefined) {
      acc[key] = transformReportRecursive(report[key], value, entireReport);
    }
    return acc;
  }, { ...report });
  delete transformed.status_code;
  delete transformed.error;
  return transformed;
};

export const transformReport = (report, map, entireReport) => {
  const preProcessedReport = preProcessReport(report);
  const entirePreProcessedReport = preProcessReport(entireReport);
  return transformReportRecursive(preProcessedReport, map, entirePreProcessedReport);
};

export const transformMultiCheckboxOtherFromAPI = (valueArray, checkboxInitialState) => {
  let otherTextValue = '';
  const updatedState = { ...checkboxInitialState };
  valueArray.forEach(value => {
    // if the key exists on the initial state object, set it to true
    if (updatedState[value] === false) {
      updatedState[value] = true;
    // else if the key doesn't exist, it's other text input value
    } else if (!updatedState[value]) {
      otherTextValue = value;
    }
  });
  return {
    checkboxes: updatedState,
    other: otherTextValue,
  };
};

export const transformMultiOtherCheckboxForAPI = ({ checkboxes, other }) => ([
  ...Object.keys(checkboxes).filter(key => checkboxes[key]),
  trimInnerValues(other),
]);

export const TRANSFORMATION_MAP_TO_API = {
  is_ninety_day_review: (is_ninety_day_review, report) => report.form_is_ninety_day_review,
  notification_days: days => 90 - Number(days),
  activity_information: {
    activity_end_date: date => validISOStringOrNull(date, 'MM/DD/YYYY'),
    activity_start_date: date => validISOStringOrNull(date, 'MM/DD/YYYY'),
    amount_involved: amount => nullOrFormatToInteger(amount),
    commodity_type: trimInnerValues,
    cumulative_amount: amount => nullOrFormatToInteger(amount),
    cusip_number: trimInnerValues,
    cyber_frauds: cyberFrauds => (
      cyberFrauds.map(({ sar_cyber_event_date, ...rest }) => ({
        sar_cyber_event_date: validISOStringOrNull(sar_cyber_event_date),
        ...trimInnerValues(rest),
      }))
    ),
    fraud_types: fraudTypes => Object.entries(fraudTypes).reduce(
      (acc: Array<string>, [typeKey, typeValues]) => {
        const selectedValues = Object.entries(typeValues).reduce(
          (allValues, [fraudId, isSelected]) => (isSelected === true
            ? [...allValues, Number(fraudId)] : allValues),
          []);
        const otherValue = Object.entries(typeValues).find(object => object[0] === 'other_value')?.[1];

        return {
          ...acc,
          [typeKey]: {
            selected_values: selectedValues,
            other_value: trimInnerValues(otherValue) || null,
          },
        };
      }, { ...fraudTypes },
    ),
    ip_addresses: trimInnerValues,
    market_traded: trimInnerValues,
    narrative: trimInnerValues,
    product_description: trimInnerValues,
  },
  entities: (entities, report) => entities.map(entity => transformReport(entity, {
    action_date: trimInnerValues,
    alternate_names_encrypted: trimInnerValues,
    dob_encrypted: trimInnerValues,
    email_addresses_encrypted: trimInnerValues,
    entity_addresses_encrypted: trimInnerValues,
    financial_tins: trimInnerValues,
    first_name_encrypted: trimInnerValues,
    gender_encrypted: trimInnerValues,
    last_name_encrypted: trimInnerValues,
    middle_name_encrypted: trimInnerValues,
    naics_code: trimInnerValues,
    occupation: trimInnerValues,
    phone_numbers_encrypted: trimInnerValues,
    relationship_tins: relationshipTins => (
      relationshipTins.map(({ position, ...rest }) => ({
        ...trimInnerValues(rest),
        position: transformCheckboxObject(position),
      }))
    ),
    sar_entity_ids: trimInnerValues,
    statement_in_activity: statementInActivity => JSON.parse(statementInActivity),
    tax_id_encrypted: trimInnerValues,
    tin: trimInnerValues,
    websites: trimInnerValues,
  }, report)),
  sar_activity_institution: {
    address: trimInnerValues,
    alternate_name: trimInnerValues,
    branch_information: branchInformation => (
      branchInformation.map(({
        branch_role,
        ...rest
      }) => ({
        ...trimInnerValues(rest),
        branch_role: transformCheckboxObject(branch_role),
      }))
    ),
    financial_institution_role: role => transformCheckboxObject(role),
    legal_name: trimInnerValues,
    loss_financial_institution: loss => nullOrFormatToInteger(loss),
    internal_control_number: trimInnerValues,
    tin: trimInnerValues,
    type_of_financial_institution: trimInnerValues,
    type_of_gaming_institution: gamingInstitution => (
      transformMultiOtherCheckboxForAPI(gamingInstitution)
    ),
    type_of_securities_institution: securitiesInstitution => (
      transformMultiOtherCheckboxForAPI(securitiesInstitution)
    ),
  },
  filing_institution: {
    address: trimInnerValues,
    alternate_name: trimInnerValues,
    contact_agency: trimInnerValues,
    contact_date: trimInnerValues,
    contact_name: trimInnerValues,
    filer_name: trimInnerValues,
    filing_institution_contact_number: trimInnerValues,
    filing_institution_contact_office: trimInnerValues,
    institution_id: trimInnerValues,
    internal_control_number: trimInnerValues,
    phone_number: trimInnerValues,
    tin: trimInnerValues,
    type_of_financial_institution: trimInnerValues,
    type_of_securities_institution: securitiesInstitution => (
      transformMultiOtherCheckboxForAPI(securitiesInstitution)
    ),
  },
};

export const transformFraudTypes = fraudTypeData => {
  // old schema contract; it's an array with ids e.g. ["1","2","6"]
  if (Array.isArray(fraudTypeData)) {
    return fraudTypeData.reduce((acc, fraudId) => {
      const section = FRAUD_ID_SECTION_MAP[fraudId];
      return {
        ...acc,
        [section]: {
          ...acc[section],
          [fraudId]: true,
        },
      };
    }, { ...initialFraudTypes() });
  }

  // if not an array, it uses the new schema contract that is nested objects
  const initialFraudTypeObject = initialFraudTypes();
  return Object.entries(fraudTypeData).reduce((acc, typeData: [
    string, { selected_values: number[]; other_value: string },
  ]) => {
    const typeKey = typeData[0];
    const sectionData = typeData[1].selected_values.reduce((values, valueId) => (
      {
        ...values,
        [valueId]: true,
      }
    ), { ...initialFraudTypeObject[typeKey] });
    const otherValue = typeData[1].other_value || '';

    return {
      ...acc,
      [typeKey]: {
        ...acc[typeKey],
        ...sectionData,
        other_value: otherValue,
      },
    };
  }, { ...initialFraudTypeObject });
};

const transformCheckboxArrayBasedOnDimension = (checkboxArray, checkboxInitial) => {
  if (checkboxArray.length > 0) {
    const checkboxData = Array.isArray(checkboxArray[0])
      ? checkboxArray[0] // this is an old schema data
      : checkboxArray;

    return checkboxData.reduce((acc, value) => { // if there are no checkboxes selected, the value comes back from the API as false
      acc[value] = true;
      return acc;
    }, { ...checkboxInitial });
  }

  return checkboxInitial;
};

export const TRANSFORMATION_MAP_FROM_API = (userContext: Partial<UserContextValue>) => ({
  form_is_ninety_day_review: (form_is_ninety_day_review, report) => report.is_ninety_day_review,
  notification_days: days => (days !== null ? `${90 - days}` : `${90 - (userContext?.customer?.sarReminderDefaultDays || 83)}`), // set 7 days if no customer default is set
  activity_information: {
    activity_start_date: date => utcDateToUsFormat(date),
    activity_end_date: date => utcDateToUsFormat(date),
    amount_involved: amount => nullOrFormatToCurrency(amount),
    commodity_type: types => prefillArrayIfEmpty(types, ''),
    cumulative_amount: amount => nullOrFormatToCurrency(amount),
    cusip_number: numbers => prefillArrayIfEmpty(numbers, ''),
    filing_note: filingNote => emptyStringIfNotString(filingNote),
    market_traded: markets => prefillArrayIfEmpty(markets, ''),
    narrative: narrative => emptyStringIfNotString(narrative),
    product_description: descriptions => prefillArrayIfEmpty(descriptions, ''),
    ip_addresses: ipAddresses => (
      prefillArrayIfEmpty(ipAddresses, { ...IP_ADDRESSES_INITIAL })
    ),
    cyber_frauds: cyberFrauds => (
      prefillArrayIfEmpty(
        cyberFrauds?.map(({ sar_cyber_event_date, ...rest }) => ({
          sar_cyber_event_date: utcDateToUsFormat(sar_cyber_event_date),
          ...rest,
        })),
        { ...CYBER_FRAUDS_INITIAL },
      )
    ),
    fraud_types: fraudIds => transformFraudTypes(fraudIds),
  },
  entities: entities => entities?.map((entity, report) => transformReport(entity, {
    alternate_names_encrypted: names => prefillArrayIfEmpty(names, ''),
    email_addresses_encrypted: emails => prefillArrayIfEmpty(emails, ''),
    phone_numbers_encrypted: numbers => prefillArrayIfEmpty(numbers, { ...PHONE_NUMBERS_INITIAL }),
    websites: websites => prefillArrayIfEmpty(websites, ''),
    relationship_tins: relationshipTins => (
      prefillArrayIfEmpty(relationshipTins?.map(({ position, ...rest }) => ({
        ...rest,
        position: transformCheckboxArrayBasedOnDimension(position, QUESTION_24_CHECKBOX_INITIAL),
      })), { ...RELATIONSHIP_TINS_INITIAL })
    ),
    entity_addresses_encrypted: entityAddresses => (
      prefillArrayIfEmpty(entityAddresses, { ...ENTITY_ADDRESS_INITIAL })
    ),
    financial_tins: financialTins => {
      // backwards compatible for old reports missing the nested additive section shape
      const transformedTins = prefillArrayIfEmpty(financialTins, { ...FINANCIAL_TINS_INITIAL });
      return transformedTins.map(({
        accounts,
        account_number,
        closed,
        ...rest
      }) => ({
        accounts: accounts || [{ account_number, closed }],
        ...rest,
      }));
    },
    sar_entity_ids: sarEntityIds => (
      prefillArrayIfEmpty(sarEntityIds, { ...SAR_ENTITY_IDS_INITIAL })
    ),
    statement_in_activity: statementInActivity => JSON.stringify(statementInActivity),
  }, report)) || [{ ...initialSarEntity() }],
  sar_activity_institution: {
    financial_institution_role: role => (
      transformCheckboxArrayBasedOnDimension(role, QUESTION_56_CHECKBOX_INITIAL)
    ),
    loss_financial_institution: loss => nullOrFormatToCurrency(loss),
    branch_information: branchInformation => (
      prefillArrayIfEmpty(branchInformation?.map(({
        branch_role,
        ...rest
      }) => ({
        ...rest,
        branch_role: branch_role
          ? branch_role.reduce((acc, value) => {
            acc[value] = true;
            return acc;
          }, { ...QUESTION_68_CHECKBOX_INITIAL })
          : QUESTION_68_CHECKBOX_INITIAL,
      })), { ...BRANCH_INFORMATION_INITIAL })
    ),
    type_of_gaming_institution: gamingInstitution => (
      Array.isArray(gamingInstitution)
        ? transformMultiCheckboxOtherFromAPI(
          gamingInstitution,
          { ...QUESTION_53_CHECKBOX_INITIAL },
        ) : gamingInstitution
    ),
    type_of_securities_institution: securitiesInstitution => (
      Array.isArray(securitiesInstitution)
        ? transformMultiCheckboxOtherFromAPI(
          securitiesInstitution,
          { ...QUESTION_54_CHECKBOX_INITIAL },
        ) : securitiesInstitution
    ),
  },
  filing_institution: {
    type_of_securities_institution: securitiesInstitution => (
      Array.isArray(securitiesInstitution)
        ? transformMultiCheckboxOtherFromAPI(
          securitiesInstitution,
          { ...QUESTION_80_CHECKBOX_INITIAL },
        ) : securitiesInstitution
    ),
  },
});

export const TRANSFORMATION_MAP_FROM_PREFILL = (userContext: Partial<UserContextValue>) => ({
  notification_days: () => `${90 - (userContext?.customer?.sarReminderDefaultDays || 83)}`, // set 7 days if no customer default is set
  activity_information: {
    commodity_type: types => prefillArrayIfEmpty(types, ''),
    cumulative_amount: amount => nullOrFormatToCurrency(amount),
    cusip_number: numbers => prefillArrayIfEmpty(numbers, ''),
    filing_note: filingNote => emptyStringIfNotString(filingNote),
    market_traded: markets => prefillArrayIfEmpty(markets, ''),
    narrative: narrative => emptyStringIfNotString(narrative),
    product_description: descriptions => prefillArrayIfEmpty(descriptions, ''),
    ip_addresses: ipAddresses => (
      prefillArrayIfEmpty(ipAddresses, { ...IP_ADDRESSES_INITIAL })
    ),
    cyber_frauds: cyberFrauds => (
      prefillArrayIfEmpty(
        cyberFrauds.map(({ sar_cyber_event_date, ...rest }) => ({
          sar_cyber_event_date: utcDateToUsFormat(sar_cyber_event_date),
          ...rest,
        })),
        { ...CYBER_FRAUDS_INITIAL },
      )
    ),
    fraud_types: fraudIds => transformFraudTypes(fraudIds),
  },
  entities: entities => entities?.map((entity, report) => transformReport(entity, {
    alternate_names_encrypted: names => prefillArrayIfEmpty(names, ''),
    email_addresses_encrypted: numbers => prefillArrayIfEmpty(numbers, ''),
    phone_numbers_encrypted: numbers => prefillArrayIfEmpty(numbers, { ...PHONE_NUMBERS_INITIAL }),
    websites: websites => prefillArrayIfEmpty(websites, ''),
    relationship_tins: relationshipTins => (
      relationshipTins.length === 0
        ? [{ ...RELATIONSHIP_TINS_INITIAL }] : relationshipTins.map(({ position, ...rest }) => ({
          ...rest,
          position: transformCheckboxArrayBasedOnDimension(position, QUESTION_24_CHECKBOX_INITIAL),
        }))
    ),
    entity_addresses_encrypted: entityAddresses => (
      prefillArrayIfEmpty(entityAddresses, { ...ENTITY_ADDRESS_INITIAL })
    ),
    financial_tins: financialTins => {
      // backwards compatible for old reports missing the nested additive section shape
      const transformedTins = prefillArrayIfEmpty(financialTins, { ...FINANCIAL_TINS_INITIAL });
      return transformedTins.map(({
        accounts,
        account_number,
        closed,
        ...rest
      }) => ({
        accounts: accounts || [{ account_number, closed }],
        ...rest,
      }));
    },
    sar_entity_ids: sarEntityIds => (
      prefillArrayIfEmpty(sarEntityIds, { ...SAR_ENTITY_IDS_INITIAL })
    ),
  }, report)) || [{ ...initialSarEntity() }],
  sar_activity_institution: {
    financial_institution_role: role => (
      transformCheckboxArrayBasedOnDimension(role, QUESTION_56_CHECKBOX_INITIAL)
    ),
    type_of_gaming_institution: gamingInstitution => (
      Array.isArray(gamingInstitution)
        ? transformMultiCheckboxOtherFromAPI(
          gamingInstitution,
          { ...QUESTION_53_CHECKBOX_INITIAL },
        ) : gamingInstitution
    ),
    type_of_securities_institution: securitiesInstitution => (
      Array.isArray(securitiesInstitution)
        ? transformMultiCheckboxOtherFromAPI(
          securitiesInstitution,
          { ...QUESTION_54_CHECKBOX_INITIAL },
        ) : securitiesInstitution
    ),
  },
  filing_institution: {
    type_of_securities_institution: securitiesInstitution => (
      Array.isArray(securitiesInstitution)
        ? transformMultiCheckboxOtherFromAPI(
          securitiesInstitution,
          { ...QUESTION_80_CHECKBOX_INITIAL },
        ) : securitiesInstitution
    ),
  },
});

// metadata helpers:
export const augmentMetadata = (
  initialMetadata: any,
  metadataResponse: any,
  report: any,
  instanceOfCurrentPage = 0,
) => (
  Object.entries(initialMetadata).reduce((acc, [key, initialValue]) => {
    // there are three possible shapes that the shape under key can be--
    // either an array of sar metadata (only for backwards compatibility),
    // an object of arrays of sar metadata, or an array of objects of arrays of sar metadata

    // if the current key is also a key within the report data, then we know it's a page/tab key
    if (report[key]) {
      // if the value is an array, it's an additive page
      if (Array.isArray(initialValue)) {
        // initialize the newMetadata from metadata response if it exists, or the initial shape otherwise
        const newMetadata = metadataResponse[key]
          ? [...metadataResponse[key]]
          : [{ ...initialValue[0] }];
        // if the metadata array is not the same length as the report data, we can add additional initial shapes
        if (newMetadata.length !== report[key].length) {
          // for each index that we are missing in the metadata
          for (let i = newMetadata.length; i < report[key].length; i += 1) {
            // we can copy over a copy of the first index the initial value into the new metadata
            // this will have duplicated keys but we will map through and recurse with this function to generate new keys
            newMetadata.push({ ...initialValue[0] });
          }
        }

        acc[key] = newMetadata.map((newPageMetadata, pageIndex) => (
          augmentMetadata(initialValue[0], newPageMetadata, report, pageIndex)
        ));
      // else if it's not an additive page
      } else {
        // we can augment or initialize the metadata if it exists or doesn't exist, respectively
        acc[key] = augmentMetadata(initialValue, metadataResponse[key] || initialValue, report);
      }
    }

    // if it is a non-page metadata array, we can map through the metadata and update it
    if (!report[key] && Array.isArray(initialValue)) {
      // if it's a nested additive section
      if (NESTED_ADDITIVE_PARENT_MAPPINGS[key]) {
        // e.g. - multiple financial tin sections with multiple additive account sections each
        // this array is where we'll build our nested additive metadata
        const newMetadata = [];
        // first, look up how many financial tin sections there are based on the route provided in NESTED_ADDITIVE_PARENT_MAPPINGS
        const additiveParentSectionValue = getValueFromPath({
          lookupObject: report,
          path: NESTED_ADDITIVE_PARENT_MAPPINGS[key],
          instanceOfCurrentPage,
        });
        // then we'll map through the financial tin sections
        additiveParentSectionValue.forEach((parentSection, parentSectionIndex) => {
          // and we can now look up the nested accounts array value with the parentSectionIndex
          const directAdditiveParentValue = getValueFromPath({
            lookupObject: report,
            path: ADDITIVE_PARENT_MAPPINGS[key],
            instanceOfCurrentPage,
            parentSectionIndex,
          });
          // and we can also check if metadata exists for this section
          // i.e. if additiveParentSectionValue is an array of 2 additive sections,
          // we expect the metadata to be shaped like [[...], [...]],
          // so we're going to either grab the existing inner metadata array and augment missing errors/keys to it,
          // or just initialize it as an empty array
          const metadataArray = metadataResponse?.[key]?.[parentSectionIndex]
            ? metadataResponse?.[key]?.[parentSectionIndex].map(value => {
              const newValue = { ...value };
              if (!value.errors) {
                newValue.errors = {};
              }
              if (!value.key) {
                newValue.key = shortid.generate();
              }
              return newValue;
            })
            : [];
          // regardless of how the metadataArray is initialize in the above block,
          // we're going to map through and create an initialMetadataShape for any missing parts
          if (
            directAdditiveParentValue
            && directAdditiveParentValue.length > metadataArray.length
          ) {
            for (let i = metadataArray.length; i < directAdditiveParentValue.length; i += 1) {
              metadataArray.push({ ...initialMetadataShape() });
            }
          }
          // and then once we're sure this inner metadata is good to go, we can set it in the parent metadata array
          newMetadata[parentSectionIndex] = metadataArray;
        });
        acc[key] = newMetadata;
      } else {
        const metadataArray = metadataResponse[key]
          ? metadataResponse[key].map(value => {
            const newValue = { ...value };
            if (!value.errors) {
              newValue.errors = {};
            }
            if (!value.key) {
              newValue.key = shortid.generate();
            }
            return newValue;
          })
          : [...initialValue];
          // for additive section inputs only
        // if there is saved report data for multiple added sections but no saved metadata,
        // we need to initialize a metadata array of the same length as the report data
        // otherwise the page can crash when attempting to render these fields
        if (ADDITIVE_PARENT_MAPPINGS[key]) {
          const parentValue = getValueFromPath({
            lookupObject: report,
            path: ADDITIVE_PARENT_MAPPINGS[key],
            instanceOfCurrentPage,
          });
          if (parentValue && parentValue.length > metadataArray.length) {
            for (let i = metadataArray.length; i < parentValue.length; i += 1) {
              metadataArray.push({ ...initialMetadataShape() });
            }
          }
        }
        acc[key] = metadataArray;
      }
    }
    return acc;
  }, {})
);

export const outgoingCompatibilinator = stateReport => ({
  ...stateReport,
  ...stateReport.activity_information,
  sar_filing_institution: stateReport.filing_institution,
});

export const outgoingMetadataCompatibilinator = metadata => ({
  ...metadata,
  ...metadata.activity_information,
  ...metadata.sar_activity_institution,
  ...metadata.filing_institution,
});

export const incomingCompatibilinator = apiReport => {
  const {
    // activity information
    activity_information: activityInformation,
    activity_end_date: activityEndDate,
    activity_start_date: activityStartDate,
    amount_involved: amountInvolved,
    no_amount_involved: noAmountInvolved,
    commodity_type: commodityType,
    cumulative_amount: cumulativeAmount,
    cusip_number: cusipNumber,
    cyber_frauds: cyberFrauds,
    filing_note: filingNote,
    fraud_types: fraudTypes,
    ip_addresses: ipAddresses,
    market_traded: marketTraded,
    narrative,
    product_description: productDescription,
    selected_transactions: selectedTransactions,
    // filing institution
    filing_institution: filingInstitution,
    sar_filing_institution: sarFilingInstitution,
    is_ninety_day_review: isNinetyDayReview,
    ...rest
  } = apiReport;

  return {
    is_ninety_day_review: isNinetyDayReview,
    form_is_ninety_day_review: isNinetyDayReview,
    activity_information: activityInformation || {
      activity_end_date: activityEndDate,
      activity_start_date: activityStartDate,
      amount_involved: amountInvolved,
      no_amount_involved: noAmountInvolved,
      commodity_type: commodityType,
      cumulative_amount: cumulativeAmount,
      cusip_number: cusipNumber,
      cyber_frauds: cyberFrauds,
      filing_note: filingNote,
      fraud_types: fraudTypes,
      ip_addresses: ipAddresses,
      market_traded: marketTraded,
      narrative,
      product_description: productDescription,
      selected_transactions: selectedTransactions,
    },
    filing_institution: filingInstitution || sarFilingInstitution,
    ...rest,
  };
};

export const incomingMetadataCompatibilinator = metadata => {
  const {
    // activity information
    activity_information: activityInformation,
    question_2: question2,
    question_29_amount: question29Amount,
    question_29_no_amount: question29NoAmount,
    question_30_from: question30From,
    question_30_to: question30To,
    question_32: question32,
    question_32_other: question32Other,
    question_33: question33,
    question_33_other: question33Other,
    question_34: question34,
    question_34_other: question34Other,
    question_35: question35,
    question_35_other: question35Other,
    question_36: question36,
    question_36_other: question36Other,
    question_37: question37,
    question_37_other: question37Other,
    question_38: question38,
    question_38_other: question38Other,
    question_39: question39,
    question_39_other: question39Other,
    question_40: question40,
    question_40_other: question40Other,
    question_41: question41,
    question_41_other: question41Other,
    question_42: question42,
    question_42_other: question42Other,
    ip_address_subheader: ipAddressSubheader,
    question_43_additive: question43Additive,
    question_43_date: question43Date,
    question_43_ip_address: question43IpAddress,
    question_43_timestamp: question43Timestamp,
    cyber_event_subheader: cyberEventSubheader,
    question_44: question44,
    question_44_additive: question44Additive,
    question_44_event_date: question44EventDate,
    question_44_event_type: question44EventType,
    question_44_event_value: question44EventValue,
    question_44_event_type_other: question44EventTypeOther,
    question_45: question45,
    question_45_other: question45Other,
    question_46: question46,
    question_46_other: question46Other,
    question_47: question47,
    question_47_additive: question47Additive,
    question_48: question48,
    question_48_additive: question48Additive,
    question_49: question49,
    question_49_additive: question49Additive,
    question_50: question50,
    question_50_additive: question50Additive,
    narrative,
    // activity institution
    sar_activity_institution: sarActivityInstitution,
    question_51_type: question51Type,
    question_51_other: question51Other,
    question_52: question52,
    question_57: question57,
    question_59_tin: question59Tin,
    question_60: question60,
    question_61_address: question61Address,
    question_62_city: question62City,
    question_63_state: question63State,
    question_64_zip: question64Zip,
    question_65_country: question65Country,
    branch_information_additive: branchInformationAdditive,
    branch_subheader: branchSubheader,
    question_68_role: question68Role,
    question_69_address: question69Address,
    question_70_rssd: question70Rssd,
    question_71: question71,
    question_71_city: question71City,
    question_72: question72,
    question_72_state: question72State,
    question_73_zip: question73Zip,
    question_74_country: question74Country,
    // filing institution
    filing_institution: filingInstitution,
    question_75: question75,
    question_76: question76,
    question_77_tin: question77Tin,
    question_78: question78,
    question_79_type: question79Type,
    question_79_other: question79Other,
    question_82_address: question82Address,
    question_83_city: question83City,
    question_84_state: question84State,
    question_85_zip: question85Zip,
    question_86_country: question86Country,
    question_93: question93,
    question_94: question94,
    question_94_ext: question94Ext,
  } = metadata;

  return {
    ...metadata,
    activity_information: activityInformation || {
      question_2: question2,
      question_29_amount: question29Amount,
      question_29_no_amount: question29NoAmount,
      question_30_from: question30From,
      question_30_to: question30To,
      question_32: question32,
      question_32_other: question32Other,
      question_33: question33,
      question_33_other: question33Other,
      question_34: question34,
      question_34_other: question34Other,
      question_35: question35,
      question_35_other: question35Other,
      question_36: question36,
      question_36_other: question36Other,
      question_37: question37,
      question_37_other: question37Other,
      question_38: question38,
      question_38_other: question38Other,
      question_39: question39,
      question_39_other: question39Other,
      question_40: question40,
      question_40_other: question40Other,
      question_41: question41,
      question_41_other: question41Other,
      question_42: question42,
      question_42_other: question42Other,
      ip_address_subheader: ipAddressSubheader,
      question_43_additive: question43Additive,
      question_43_date: question43Date,
      question_43_ip_address: question43IpAddress,
      question_43_timestamp: question43Timestamp,
      cyber_event_subheader: cyberEventSubheader,
      question_44: question44,
      question_44_additive: question44Additive,
      question_44_event_date: question44EventDate,
      question_44_event_type: question44EventType,
      question_44_event_value: question44EventValue,
      question_44_event_type_other: question44EventTypeOther,
      question_45: question45,
      question_45_other: question45Other,
      question_46: question46,
      question_46_other: question46Other,
      question_47: question47,
      question_47_additive: question47Additive,
      question_48: question48,
      question_48_additive: question48Additive,
      question_49: question49,
      question_49_additive: question49Additive,
      question_50: question50,
      question_50_additive: question50Additive,
      narrative,
    },
    sar_activity_institution: sarActivityInstitution || {
      question_51_type: question51Type,
      question_51_other: question51Other,
      question_52: question52,
      question_57: question57,
      question_59_tin: question59Tin,
      question_60: question60,
      question_61_address: question61Address,
      question_62_city: question62City,
      question_63_state: question63State,
      question_64_zip: question64Zip,
      question_65_country: question65Country,
      branch_information_additive: branchInformationAdditive,
      branch_subheader: branchSubheader,
      question_68_role: question68Role,
      question_69_address: question69Address,
      question_70_rssd: question70Rssd,
      question_71: question71,
      question_71_city: question71City,
      question_72: question72,
      question_72_state: question72State,
      question_73_zip: question73Zip,
      question_74_country: question74Country,
    },
    filing_institution: filingInstitution || {
      question_75: question75,
      question_76: question76,
      question_77_tin: question77Tin,
      question_78: question78,
      question_79_type: question79Type,
      question_79_other: question79Other,
      question_82_address: question82Address,
      question_83_city: question83City,
      question_84_state: question84State,
      question_85_zip: question85Zip,
      question_86_country: question86Country,
      question_93: question93,
      question_94: question94,
      question_94_ext: question94Ext,
    },
  };
};
